import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, InternalFieldName, useForm } from 'react-hook-form';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import Form from '../../components/Crud/Form';
import { useQuery } from '../../services/utils';
import CustomButton from '../../components/CustomButton/CustomButton';
import CustomSelect from '../../components/CustomSelect/CustomSelect';

export interface EstimateFormFields {
  customer: number;
  company: number;
  transactionNature: number;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function EstimatesNew() {
  const {
    control,
    setValue,
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<EstimateFormFields>({ mode: 'onBlur' });
  const {
    user: { organization },
  } = useAuth();
  const navigate = useNavigate();
  const query = useQuery();
  const [formErrors, setFormErrors] = useState({});

  const onSubmit = async (data: EstimateFormFields) => {
    try {
      const response = await api.post(
        `organizations/${organization.id}/estimates`,
        data
      );
      const { id } = response.data;

      navigate(`/estimates/${id}/edit`);
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        setFormErrors(e.response.data.errors);
      } else {
        throw e;
      }
    }
  };

  const [formFields, setFormFields] = useState<EstimateFormFields>(
    {} as EstimateFormFields
  );

  useEffect(() => {
    reset(formFields);
  }, [formFields]);

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactionNatures, setTransactionNatures] = useState([]);

  async function getCompanies() {
    if (organization) {
      const response = await api.get(
        `organizations/${organization.id}/companies`
      );

      setCompanies(response.data.data);
    }
  }

  async function getCustomers() {
    if (organization) {
      const response = await api.get(
        `organizations/${organization.id}/customers`
      );
      setCustomers(response.data.data);
      const customerId = query.get('customerId');
      if (customerId) {
        reset({ customer: Number(customerId) });
      }
    }
  }

  async function getTransactionNatures() {
    if (organization) {
      const response = await api.get(
        `organizations/${organization.id}/transaction-natures`
      );
      setTransactionNatures(response.data.data);
    }
  }

  useEffect(() => {
    getCompanies();
    getCustomers();
    getTransactionNatures();
  }, [organization]);

  return (
    <Form isLoading={false} title='Novo Orçamento' onSubmitCallback={() => {}}>
      <form>
        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            Empresa
          </label>
          <select
            required
            className='form-select'
            aria-label='Selecione a Empresa'
            {...register('company', { required: true })}
          >
            <option value=''>Selecione a Empresa</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.company && (
            <div className='form-text text-danger'>
              {errors.company.message}
            </div>
          )}
        </div>

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className='form-label'>Cliente</label>
          <Controller
            name='customer'
            control={control}
            render={({ field }) => (
              <CustomSelect
                options={customers.map((customer: any) => ({
                  label: customer.name,
                  value: customer.id,
                }))}
                value={field.value ?? ''}
                onChange={field.onChange}
                placeholder='Selecione o Cliente'
              />
            )}
          />
        </div>

        <div className='mb-3'>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label'>
            Natureza da Transação
          </label>
          <select
            required
            className='form-select'
            aria-label='Selecione a Natureza da Transação'
            {...register('transactionNature', { required: true })}
          >
            <option value=''>Selecione a Natureza da Transação</option>
            {transactionNatures.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.transactionNature && (
            <div className='form-text text-danger'>
              {errors.transactionNature.message}
            </div>
          )}
        </div>

        <div className='d-flex justify-content-end gap-2'>
          <Link to='/estimates' className='btn btn-secondary'>
            Cancelar
          </Link>
          <CustomButton
            type='submit'
            className='btn btn-primary'
            handleClick={handleSubmit(onSubmit)}
          >
            Salvar
          </CustomButton>
        </div>
      </form>
    </Form>
  );
}
